/**
 * MCP 落地页（/ai/chatgpt、/ai/claude、/ai/codex）共享的 i18next 取词器。
 * 用法：各页 constants.js 在 getConstants 内 `var i18next = require('../mcpLanding/i18nText').getI18next();`
 *       再用 i18next 的 t(key, default) 取词（key 形如 {pageId}-hero-title）。
 *       注意：本文件不要写出形如 i18next.t('真实key', ...) 的字面调用，否则会被翻译提取器误匹配并归到 /common。
 * 规则与 odm/constants.js 一致：
 * - SSR/CSR 一致：未初始化（或非浏览器）时回退到默认英文（fallback.t 直接返回第二个参数）。
 * - keySeparator/nsSeparator 用占位串，避免 key 里的 '.'/':' 被当成嵌套分隔。
 * 同时运行在 Node 与浏览器，使用 CommonJS。
 */
/**
 * 把 `{{name}}` 占位替换成 values 里的取值。
 *
 * 【为什么 fallback 也必须会插值】SSR 与「i18next 尚未初始化」时走的是下面的 fallback.t，
 * 它原样返回第二个参数。而真实 i18next **会**对 `{{}}` 做插值 —— 两边行为不一致的话，
 * 凡是用了 `{{}}` 的词条在服务端会直接吐出字面量 `{{wf}}`，客户端 hydrate 后才变成正常文字，
 * 既有肉眼可见的闪烁，也是 hydration 不一致。
 *
 * 【取不到值时保留占位符，而不是吞成空】与 i18next 默认行为相反，这是刻意的：
 * 忘传取值时页面上会明晃晃出现 `{{wf}}`，单测 CASE-C2b（渲染产物不得含 `{{`）立刻变红；
 * 若吞成空则是静默丢字，正是最早那个"强调词整段消失"的坑。
 *
 * @param {string} text 含 {{name}} 占位的文本
 * @param {Object} [values] 取值表
 * @returns {string}
 */
function interpolate(text, values) {
  if (typeof text !== 'string' || !values) return text;
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, function (whole, name) {
    return Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : whole;
  });
}

var fallback = {
  t: function (key, defaultValue, options) {
    return interpolate(defaultValue, options);
  },
};

/**
 * 剔除「没有任何文字内容」的译文，让 t(key, default) 回退到英文源。
 *
 * 【为什么需要】机翻偶尔会把整句话吞掉、只吐回一个标签。260818 实测：
 *   worldfirst-faq-q4-a2-v2 的英文源是
 *     `<strong>Please note:</strong> the bank account must have the same owner as the WorldFirst account.`
 *   而 kk / tg / uz 三个语种拉回来的译文**整条就是** `"<Strong>"`。
 * i18next 认为该 key 有译文，于是 t() 返回它，页面上那段 FAQ 提示是一片空白 ——
 * 这比"没翻译"更糟：没翻译至少能看到英文。
 *
 * 【判据刻意只针对"非空但无文字"】即 `<Strong>` 这种形态。**空字符串不在处理范围内**：
 * 全站语言包实测有 212 条译文是空串（common 98、mine-authorize 32、home 26…），
 * 它们现状就是渲染空白，可能是某些语种有意留空；把它们一并回退成英文会波及 8 个共用本取词器的
 * 页面、212 条文案，那是另一个需要单独拍板的问题，不该顺手夹带。
 * 而"非空但只剩标签"全站只有 4 条，全部是本次机翻事故的产物，形态明确、修掉无副作用。
 *
 * 放在取词器这一层而不是各页组件里：任何页面、任何 key 都可能碰到，堵一次就够。
 *
 * @param {Object} lng 词库对象（window.LNG）
 * @returns {Object} 过滤后的词库
 */
function withoutEmptyTranslations(lng) {
  var out = {};
  Object.keys(lng || {}).forEach(function (k) {
    var v = lng[k];
    // 非空（有实际字符）但去掉标签后不剩文字 → 这不是译文，丢弃以回退英文源
    if (typeof v === 'string' && v.trim() && !v.replace(/<[^>]*>/g, '').trim()) return;
    out[k] = v;
  });
  return out;
}

function getI18next() {
  if (
    typeof window !== 'undefined' &&
    window.i18next &&
    !window.i18next.isInitialized &&
    window.LNG
  ) {
    var lng = (document.cookie.match(/(?:^|;\s*)lng=([^;]*)/) || [])[1] || 'en';
    window.i18next.init({
      lng: lng,
      debug: false,
      resources: { [lng]: { translation: withoutEmptyTranslations(window.LNG) } },
      keySeparator: '__keySeparator__',
      nsSeparator: '__nsSeparator__',
    });
  }
  return typeof window !== 'undefined' && window.i18next && window.i18next.isInitialized
    ? window.i18next
    : fallback;
}

module.exports = { getI18next: getI18next };
